import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { LocalStorage } from '@/app/core/local-storage/local-storage';
import { AuthService } from './auth.service';
import { getOrCreateDeviceId } from './device-id';
import { getDemoLensId } from '@/app/core/company/demo-lens.state';
import { readPortalLangCode } from '@/app/core/i18n/transloco.config';

// Relative path — matches the same-origin /api calls (see auth.service.ts).
// URLs stay relative through the whole interceptor chain, so this prefix check
// is safe: the SSR rewrite to an absolute origin happens below us, in
// ServerApiBackend (see core/http/api-origin.ts).
const API_BASE = '/api';
const ACTIVE_COMPANY_KEY = 'melland_active_company';

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);
  const storage = inject(LocalStorage);
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Only attach token / company header for our API
  if (!req.url.startsWith(API_BASE)) {
    return next(req);
  }

  // SSR'da cihaz kimliği ÜRETİLMEZ: sunucudaki depolama istek başına boş bir
  // Map; her render yeni bir kimlik uydurur ve kullanıcıya durmadan "yeni
  // cihazdan giriş" uyarısı giderdi.
  const deviceId = isBrowser ? getOrCreateDeviceId(storage) : null;

  const token = authService.getToken();
  const authReq = decorateRequest(req, token, readActiveCompanyId(storage), deviceId);

  // SSR'da 401 yenileme/çıkış akışı ÇALIŞMAZ. Sunucuda oturum yoktur: yenilenecek
  // bir refresh token, çıkarılacak bir kullanıcı yoktur. Daha kötüsü, logout()
  // router.navigateByUrl('/auth/sign-in') çağırır — bu, render edilmekte olan
  // isteği KAÇIRIR ve vitrin sayfası yerine giriş sayfasına 302 döneriz.
  // (Tam olarak bu oldu: anonim /api/web/* 401 dönünce melbarrel.com'un tüm
  // sayfaları /auth/sign-in'e yönleniyordu.)
  // Hata bileşene ulaşsın; o zaten yedek içerik gösteriyor.
  if (!isBrowser) {
    return next(authReq);
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // On 401 try refresh once, then logout
      // /logout dışarıda: 401 alırsa yenileme→başarısız→logout() zinciri
      // kendini tekrar çağırır ve sonsuz döngüye girerdi.
      if (error.status === 401 && !req.url.includes('refresh-token') && !req.url.includes('/logout')) {
        const refresh$ = authService.refreshToken();
        if (refresh$) {
          return refresh$.pipe(
            switchMap(() => {
              const newToken = authService.getToken();
              return next(decorateRequest(req, newToken, readActiveCompanyId(storage), deviceId));
            }),
            catchError((refreshError) => {
              authService.logout();
              return throwError(() => refreshError);
            })
          );
        }
        authService.logout();
      }
      return throwError(() => error);
    })
  );
};

/**
 * Authorization + X-Active-Company-Id header'larını ekler.
 * Active company localStorage'dan okunur (signal effect'ine gerek yok,
 * her HTTP request'i bağımsız olarak güncel state'i alır).
 */
function decorateRequest(
  req: HttpRequest<unknown>,
  token: string | null,
  companyId: number | null,
  deviceId: string | null
): HttpRequest<unknown> {
  const headers = buildApiHeaders(token, companyId, deviceId);

  return Object.keys(headers).length > 0
    ? req.clone({ setHeaders: headers })
    : req;
}

/**
 * API isteğine giden başlıklar — HttpClient'ı ATLAYAN çağrılar için de.
 *
 * Melbear'ın akışlı ucu fetch + ReadableStream kullanıyor (HttpClient gövdeyi
 * parça parça vermiyor) ve bu yüzden interceptor'a HİÇ uğramıyor. Başlıkları
 * orada elle kurmak, kaçınılmaz olarak bu listeden ayrı düşerdi: cihaz kimliği
 * eklenmez, aktif şirket gitmez ve akış 403'e çarpardı. Tek kaynak burası.
 */
export function buildApiHeaders(
  token: string | null,
  companyId: number | null,
  deviceId: string | null
): Record<string, string> {
  const headers: Record<string, string> = {};

  // Oturum güvenliği: sunucu bu kimlikle oturumu cihaza bağlar ve tanımadığı
  // bir cihazdan giriş olunca kullanıcıyı uyarır.
  if (deviceId) {
    headers['X-Device-Id'] = deviceId;
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (companyId !== null) {
    headers['X-Active-Company-Id'] = String(companyId);
  }

  // Deneme merceği: sunucu bu isteği demo şirket kapsamında koşturur
  // (DemoLensMiddleware doğrular). Aktif şirket başlığı olduğu gibi kalır.
  const demoLens = getDemoLensId();
  if (demoLens !== null) {
    headers['X-Demo-Lens'] = String(demoLens);
  }

  // Arayüz dili: sunucu tarafı dile bağlı üretimler (demo şablonu, kiracının son
  // dili) tarayıcı diline değil PANEL diline bakmalı. readPortalLangCode SSR'da
  // güvenli (localStorage yoksa varsayılan döner).
  headers['Accept-Language'] = readPortalLangCode();

  return headers;
}

/** Depodan okuyup başlıkları kuran kısayol — fetch tabanlı çağrılar için. */
export function currentApiHeaders(storage: LocalStorage, token: string | null): Record<string, string> {
  return buildApiHeaders(token, readActiveCompanyId(storage), getOrCreateDeviceId(storage));
}

function readActiveCompanyId(storage: LocalStorage): number | null {
  try {
    const raw = storage.getItem(ACTIVE_COMPANY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: number };
    return typeof parsed?.id === 'number' && parsed.id > 0 ? parsed.id : null;
  } catch {
    return null;
  }
}
